import { __decorate } from "tslib";
import { Mixins, Component, Prop } from 'vue-property-decorator';
import FormMixin from '@/helpers/vue_mixins/FormMixin';
import FormValidationsMixin from '@/helpers/vue_mixins/v2/FormValidationsMixin';
import ActionButton from '@/components/action_button/Index.vue';
import PageTitle from '@/components/v2/PageTitle.vue';
import FormHeader from '@/components/v2/FormHeader.vue';
import BaseTextField from '@/components/v2/BaseTextField.vue';
import BaseTextArea from '@/components/v2/BaseTextArea';
import { required, email } from 'vuelidate/lib/validators';
import StoreDetailsBasic from '@/plugins/graphql/queries/v2/store_manager/store-details-basic.gql';
import { StoreBasicModel } from '@/helpers/entities/models/StoreBasicModel';
import { STORE_ROUTE } from '@/helpers/constants/StoreManagerRoutes';
import StoreUpdate from '@/plugins/graphql/mutations/v2/store_manager/store-update.gql';
import StoreAvatarUpdate from '@/plugins/graphql/mutations/v2/store_manager/store-avatar-update.gql';
import StoreBasicFragment from '@/plugins/graphql/fragments/v2/store_manager/store-basic.gql';
import Uploader from '@/components/v2/Uploader';
import BaseImagePlaceholder from '@/components/v2/BaseImagePlacelholder';
import StoreCategories from '@/components/v2/store_manager/StoreCategories';
import { FileUploadInputModel } from '@/helpers/entities/models/FileUploadInputModel';
import { PhotoModel } from '@/helpers/entities/models/PhotoModel';
import { omit } from 'lodash';
/**
 * Store Details Edit Page
 * @author Peter John Alvarado <peterjohn@gorated.ph>
 * @since Version {0.21.0}
 */
let StoreDetailsEdit = class StoreDetailsEdit extends Mixins(FormMixin, FormValidationsMixin) {
    constructor() {
        super(...arguments);
        this.store = null;
        this.form = {
            storeUuid: '',
            name: '',
            storeDescription: '',
            images: [],
            storeCategoryUuid: null,
            emailNotifRecipients: [],
        };
    }
    goToStorePage() {
        this.$router.push({ name: STORE_ROUTE.name });
    }
    /**
     * Valid Images Getter Method
     * Filter Not Deleted Images
     * @author Peter John Alvarado <peterjohn@gorated.ph>
     * @since Version {0.39.0}
     *
     * @return {Array<FileUploadInputImpl>}
     */
    get validImages() {
        const validImageFilter = (image) => {
            return !image.isDeleted && (!!image.file || !!image.url);
        };
        return this.form.images.filter(validImageFilter);
    }
    /**
     * Valid Images Validator
     * @author Peter John Alvarado <peterjohn@gorated.ph>
     * @since Version {0.39.0}
     *
     * @return {boolean}
     */
    get hasValidImages() {
        return this.validImages.length >= 1;
    }
    async onSubmit() {
        if (!this.hasValidImages) {
            this.errorNotification('Please Upload Store Logo');
        }
        this.$v.$touch();
        if (!this.$v.$invalid && this.hasValidImages) {
            const images = this.validImages;
            await this.update()
                .then(async () => {
                // Pass Images as method argument to avoid issue on not uploaded
                // avatar for new stores
                await this.avatarUpdate(images).then(() => {
                    this.successNotification('Store Successfully Updated');
                    this.goToStorePage();
                });
            })
                .catch((e) => {
                this.errorNotification(e.message);
            })
                .finally(() => {
                this.requestDone();
            });
        }
    }
    async update() {
        const emailNotifRecipients = this.form.emailNotifRecipients.join(',');
        return this.$apollo.mutate({
            mutation: StoreUpdate,
            variables: {
                input: {
                    ...omit(this.form, 'images', 'emailNotifRecipients'),
                    emailNotifRecipients,
                },
            },
            update(cache, result) {
                const { data: { appStoreUpdate: { store }, }, } = result;
                cache.writeFragment({
                    id: store.uuid,
                    fragment: StoreBasicFragment,
                    data: store,
                    fragmentName: 'StoreBasic',
                });
            },
        });
    }
    async avatarUpdate(files) {
        const inputImages = files.map((input) => input.file);
        const images = inputImages.filter((file) => file !== null);
        if (images.length >= 1) {
            return this.$apollo.mutate({
                mutation: StoreAvatarUpdate,
                variables: {
                    input: {
                        storeUuid: this.uuid,
                        images,
                    },
                },
                update(cache, result) {
                    const { data: { appStoreAvatarUpdate: { store }, }, } = result;
                    cache.writeFragment({
                        id: store.uuid,
                        fragment: StoreBasicFragment,
                        data: store,
                        fragmentName: 'StoreBasic',
                    });
                },
            });
        }
    }
};
__decorate([
    Prop({ type: String, required: true })
], StoreDetailsEdit.prototype, "uuid", void 0);
StoreDetailsEdit = __decorate([
    Component({
        apollo: {
            store: {
                query: StoreDetailsBasic,
                variables() {
                    return {
                        appUuid: this.uuid,
                    };
                },
                update(data) {
                    return new StoreBasicModel(data.app);
                },
                result({ data }) {
                    const { uuid, name, storeDescription, storeAvatarUrl, emailNotifRecipients } = data.app;
                    const vm = this;
                    // Mock Photo Model
                    const avatar = new PhotoModel({ uuid: storeAvatarUrl, photoUrl: storeAvatarUrl });
                    Object.assign(vm.form, {
                        name,
                        storeDescription,
                        storeUuid: uuid,
                        images: [new FileUploadInputModel(avatar)],
                        storeCategoryUuid: data.app.storeCategory?.uuid,
                        emailNotifRecipients: emailNotifRecipients ? emailNotifRecipients.split(',') : [],
                    });
                },
            },
        },
        validations: {
            form: {
                name: { required },
                storeCategoryUuid: { required },
                emailNotifRecipients: {
                    $each: {
                        email,
                    },
                    required,
                },
            },
        },
        components: {
            ActionButton,
            BaseTextField,
            BaseTextArea,
            BaseImagePlaceholder,
            FormHeader,
            PageTitle,
            StoreCategories,
            Uploader,
        },
    })
], StoreDetailsEdit);
export default StoreDetailsEdit;
